/**
 * AdminAIDesignEngine.jsx
 *
 * AI Design Engine — Admin Portal playground for the decision engine at
 * src/aiDesignEngine/. Composes a DesignSpec (the choices OpenAI will make
 * later), runs the pipeline fully client-side, and inspects the output:
 *
 *   1. Structured editor command stream (never SVG/HTML — commands only)
 *   2. Template validation report (overflow, overlap, margins, grammar)
 *   3. Premium quality score (0–100 + per-metric breakdown)
 *
 * This page performs no network calls and touches no existing feature.
 */
import React, { useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Cpu, Play, Copy, CheckCircle2, AlertTriangle, XCircle,
  LayoutTemplate, Palette, Type, Ruler, Shapes, ListTree, Gauge,
} from 'lucide-react';
import {
  generateTemplate,
  listLayoutPresets,
  listFontPairings,
  listDensities,
  listDecorationStyles,
  HARMONIES,
  SCALE_RATIOS,
} from '../../aiDesignEngine';

const CONTENT_FIELDS = [
  { key: 'primaryHeading', label: 'Primary heading', placeholder: 'Summer Mega Sale' },
  { key: 'secondaryHeading', label: 'Secondary heading', placeholder: 'Everything must go' },
  { key: 'body', label: 'Body copy', placeholder: 'Up to 70% off across the entire store…' },
  { key: 'offer', label: 'Offer', placeholder: '70% OFF' },
  { key: 'cta', label: 'CTA', placeholder: 'Shop Now' },
  { key: 'footer', label: 'Footer', placeholder: 'www.yoursite.com' },
];

const CANVAS_SIZES = [
  { id: 'portrait', label: 'Flyer 1080×1350', width: 1080, height: 1350 },
  { id: 'square', label: 'Square 1080×1080', width: 1080, height: 1080 },
  { id: 'story', label: 'Story 1080×1920', width: 1080, height: 1920 },
  { id: 'a4', label: 'A4 2480×3508', width: 2480, height: 3508 },
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-600 mb-1 block">{label}</span>
    {children}
  </label>
);

const SelectBox = ({ value, onChange, options }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

const ScoreBar = ({ label, value }) => (
  <div className="flex items-center gap-2">
    <span className="w-28 text-xs text-gray-500 capitalize flex-shrink-0">{label}</span>
    <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
      <div
        className={`h-full rounded-full ${value >= 75 ? 'bg-emerald-500' : value >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
        style={{ width: `${value}%` }}
      />
    </div>
    <span className="w-8 text-right text-xs font-semibold text-gray-700">{value}</span>
  </div>
);

const AdminAIDesignEngine = () => {
  const [canvasId, setCanvasId] = useState('portrait');
  const [layout, setLayout] = useState('classic-flyer');
  const [seed, setSeed] = useState('#7c3aed');
  const [harmony, setHarmony] = useState('complementary');
  const [dark, setDark] = useState(false);
  const [pairingId, setPairingId] = useState('modern-sans');
  const [scale, setScale] = useState('balanced');
  const [spacing, setSpacing] = useState('comfortable');
  const [decoration, setDecoration] = useState('organic');
  const [content, setContent] = useState({
    primaryHeading: 'Summer Mega Sale',
    secondaryHeading: 'Everything must go',
    body: 'Up to 70% off across the entire store this weekend only.',
    offer: '70% OFF',
    cta: 'Shop Now',
    footer: 'www.yoursite.com',
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const layoutPresets = useMemo(listLayoutPresets, []);
  const fontPairings = useMemo(listFontPairings, []);

  const handleGenerate = () => {
    setError('');
    setCopied(false);
    try {
      const size = CANVAS_SIZES.find((s) => s.id === canvasId);
      const spec = {
        canvas: { width: size.width, height: size.height },
        layout,
        palette: { seed, harmony, dark },
        typography: { pairingId, baseSize: Math.round(size.width / 67), scale },
        spacing,
        decoration,
        content: Object.fromEntries(
          Object.entries(content)
            .filter(([, text]) => text.trim())
            .map(([key, text]) => [key, { text: text.trim() }])
        ),
      };
      setResult(generateTemplate(spec));
    } catch (e) {
      setResult(null);
      setError(e.message);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(result.commands, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable — ignore */ }
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Cpu className="w-6 h-6 text-purple-600" /> AI Design Engine
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Decision engine playground — outputs structured editor commands only.
              OpenAI integration will drive these choices later.
            </p>
          </div>
          <Button onClick={handleGenerate} className="bg-purple-600 hover:bg-purple-700">
            <Play className="w-4 h-4 mr-2" /> Generate Commands
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── Design spec form ── */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <LayoutTemplate className="w-4 h-4 text-purple-500" /> Design Spec
            </h2>

            <Field label="Canvas">
              <SelectBox value={canvasId} onChange={setCanvasId}
                options={CANVAS_SIZES.map((s) => ({ value: s.id, label: s.label }))} />
            </Field>
            <Field label="Layout preset">
              <SelectBox value={layout} onChange={setLayout}
                options={layoutPresets.map((p) => ({ value: p.id, label: p.name }))} />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Seed color">
                <div className="flex items-center gap-2">
                  <input type="color" value={seed} onChange={(e) => setSeed(e.target.value)}
                    className="h-9 w-10 rounded border border-gray-200 cursor-pointer" />
                  <Input value={seed} onChange={(e) => setSeed(e.target.value)} className="h-9 text-sm" />
                </div>
              </Field>
              <Field label="Harmony">
                <SelectBox value={harmony} onChange={setHarmony}
                  options={HARMONIES.map((h) => ({ value: h, label: h }))} />
              </Field>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input type="checkbox" checked={dark} onChange={(e) => setDark(e.target.checked)}
                className="rounded border-gray-300" />
              Dark background
            </label>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Font pairing">
                <SelectBox value={pairingId} onChange={setPairingId}
                  options={fontPairings.map((f) => ({ value: f.id, label: f.name }))} />
              </Field>
              <Field label="Type scale">
                <SelectBox value={scale} onChange={setScale}
                  options={Object.keys(SCALE_RATIOS).map((s) => ({ value: s, label: s }))} />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Spacing density">
                <SelectBox value={spacing} onChange={setSpacing}
                  options={listDensities().map((d) => ({ value: d, label: d }))} />
              </Field>
              <Field label="Decoration style">
                <SelectBox value={decoration} onChange={setDecoration}
                  options={listDecorationStyles().map((d) => ({ value: d, label: d }))} />
              </Field>
            </div>

            <div className="pt-2 border-t border-gray-100 space-y-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Content</h3>
              {CONTENT_FIELDS.map(({ key, label, placeholder }) => (
                <Field key={key} label={label}>
                  <Input
                    value={content[key]}
                    placeholder={placeholder}
                    onChange={(e) => setContent((prev) => ({ ...prev, [key]: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </Field>
              ))}
            </div>
          </div>

          {/* ── Results ── */}
          <div className="lg:col-span-2 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-2 text-sm text-red-700">
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
              </div>
            )}

            {!result && !error && (
              <div className="bg-white rounded-xl border border-dashed border-gray-300 p-16 text-center text-gray-400">
                <Cpu className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Configure a design spec and hit <b>Generate Commands</b>.</p>
                <p className="text-xs mt-1">The engine emits editor commands — it never draws.</p>
              </div>
            )}

            {result && (
              <>
                {/* Quality score */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <Gauge className="w-4 h-4 text-purple-500" /> Premium Quality Score
                    </h2>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-500">
                        Premium feel: <b className="text-gray-800">{result.quality.premiumFeel}</b>
                      </span>
                      <span className={`text-2xl font-bold ${result.quality.score >= 75 ? 'text-emerald-600' : result.quality.score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                        {result.quality.score}<span className="text-sm text-gray-400">/100</span>
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(result.quality.breakdown).map(([metric, value]) => (
                      <ScoreBar key={metric} label={metric} value={value} />
                    ))}
                  </div>
                </div>

                {/* Validation report */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
                    <ListTree className="w-4 h-4 text-purple-500" /> Validation Report
                    {result.validation.valid ? (
                      <span className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                        <CheckCircle2 className="w-4 h-4" /> Valid
                      </span>
                    ) : (
                      <span className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-red-600">
                        <XCircle className="w-4 h-4" /> {result.validation.errors.length} error(s)
                      </span>
                    )}
                  </h2>
                  {result.validation.issues.length === 0 ? (
                    <p className="text-sm text-gray-400">No issues — alignment, margins, overlap and grammar all pass.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {result.validation.issues.map((iss, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          {iss.severity === 'error'
                            ? <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                            : <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />}
                          <span className="text-gray-700">
                            <code className="text-xs bg-gray-100 rounded px-1 py-0.5 mr-1">{iss.code}</code>
                            {iss.message}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Tokens summary */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
                    <Palette className="w-4 h-4 text-purple-500" /> Resolved Tokens
                  </h2>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      {Object.entries(result.tokens.palette).map(([role, color]) => (
                        <div key={role} className="text-center">
                          <div className="w-9 h-9 rounded-lg border border-gray-200" style={{ background: color }} title={`${role}: ${color}`} />
                          <span className="text-[10px] text-gray-400">{role.slice(0, 4)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 space-y-0.5">
                      <p className="flex items-center gap-1"><Type className="w-3 h-3" />
                        {result.tokens.typography.display.fontFamily} / {result.tokens.typography.body.fontFamily} — display {result.tokens.typography.display.fontSize}px
                      </p>
                      <p className="flex items-center gap-1"><Ruler className="w-3 h-3" />
                        {result.spec.spacing} spacing · base {result.tokens.spacing.base}px
                      </p>
                      <p className="flex items-center gap-1"><Shapes className="w-3 h-3" />
                        {result.spec.decoration} decorations · {result.spec.layout} layout
                      </p>
                    </div>
                  </div>
                </div>

                {/* Command stream */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-gray-900">
                      Editor Command Stream
                      <span className="ml-2 text-xs font-normal text-gray-400">
                        {result.commands.length} commands
                      </span>
                    </h2>
                    <Button variant="outline" size="sm" onClick={handleCopy}>
                      {copied ? <CheckCircle2 className="w-4 h-4 mr-1 text-emerald-600" /> : <Copy className="w-4 h-4 mr-1" />}
                      {copied ? 'Copied' : 'Copy JSON'}
                    </Button>
                  </div>
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-auto max-h-96 leading-relaxed">
                    {JSON.stringify(result.commands, null, 2)}
                  </pre>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAIDesignEngine;
